'use client'

import { useEffect, useState } from 'react'

import { useFetch } from '@marketplace-web/core-api/api-client-util'
import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useFeatureSwitch } from '@marketplace-web/feature-flags/feature-switches-data'
import {
  transformFeedbackRatingsResponse,
  UpdateFeedbackRatingOptionsArgs,
} from '@marketplace-web/conversation-feedback/conversation-feedback-data'
import {
  clickEvent,
  viewScreenEvent,
  sendItemUploadFeedback,
  getItemUploadFeedbackRatings,
} from '@marketplace-web/item-upload/after-upload-actions-data'

import FeedbackFormModal from '../FeedbackFormModal'
import usePromoAfterItemUploadContext from '../../hooks/usePromoAfterItemUploadContext'

type Props = { onClose: () => void }

const ItemUploadFeedback = ({ onClose }: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { uploadedItemId: feedbackItemId, setNextPromotion } = usePromoAfterItemUploadContext()
  const isFeedbackEnabled = useFeatureSwitch('item_upload_feedback_form')
  const { track } = useTracking()

  const {
    fetch: fetchItemUploadFeedback,
    error,
    transformedData: feedbackRatings,
  } = useFetch(getItemUploadFeedbackRatings, transformFeedbackRatingsResponse)

  useEffect(() => {
    fetchItemUploadFeedback()
  }, [fetchItemUploadFeedback, track, isFeedbackEnabled])

  useEffect(() => {
    if (error) {
      setNextPromotion()

      return
    }

    if (!feedbackRatings) return

    setIsModalOpen(true)
    track(viewScreenEvent({ screen: 'item_upload_feedback_form' }))
  }, [feedbackRatings, error, track, setNextPromotion])

  const handleModalClose = (finalized = false) => {
    setIsModalOpen(false)
    setNextPromotion()

    if (finalized) return

    track(
      clickEvent({
        screen: 'item_upload_feedback_form',
        target: 'close_screen',
      }),
    )
  }

  const updateFeedbackOptions = async ({
    finalized,
    rating,
    message,
    feedbackStatementIds,
  }: UpdateFeedbackRatingOptionsArgs) => {
    if (!finalized) return

    await sendItemUploadFeedback({
      itemId: feedbackItemId,
      rating,
      finalized,
      comment: message || '',
      feedbackStatementIds,
    })
    handleModalClose(true)
    onClose()
  }

  return (
    <FeedbackFormModal
      isOpen={isModalOpen}
      ratings={feedbackRatings}
      isInputWithoutRatingOptions
      isCloseActionVisible={false}
      onClose={handleModalClose}
      onSubmit={updateFeedbackOptions}
    />
  )
}

export default ItemUploadFeedback
